import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { Youtube, ArrowRight } from 'lucide-react';
import { saveProgress, getProgress } from '../lib/progress';

// Extract Apple embed URL from a per-episode Apple Podcasts link
function getAppleEmbed(appleUrl) {
  if (!appleUrl) return null;
  const idMatch = appleUrl.match(/id(\d+)/);
  const epMatch = appleUrl.match(/[?&]i=(\d+)/);
  if (!idMatch || !epMatch) return null;
  return `https://embed.podcasts.apple.com/us/podcast/id${idMatch[1]}?i=${epMatch[1]}&theme=dark`;
}

// Load the YouTube IFrame API script exactly once, return a promise that
// resolves with the global YT object.
let ytApiPromise = null;
const loadYouTubeApi = () => {
  if (typeof window === 'undefined') return Promise.reject();
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === 'function') prev();
      resolve(window.YT);
    };
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(tag);
  });
  return ytApiPromise;
};

const YouTubePlayer = ({ videoId, episodeId, startAt = 0 }) => {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let mountedNode = null;

    const persist = () => {
      const p = playerRef.current;
      if (!p || typeof p.getCurrentTime !== 'function') return;
      try {
        const seconds = p.getCurrentTime();
        const duration = typeof p.getDuration === 'function' ? p.getDuration() : undefined;
        saveProgress(episodeId, seconds, duration);
      } catch {
        /* player not ready */
      }
    };

    loadYouTubeApi().then((YT) => {
      if (cancelled || !containerRef.current) return;
      mountedNode = document.createElement('div');
      containerRef.current.innerHTML = '';
      containerRef.current.appendChild(mountedNode);
      playerRef.current = new YT.Player(mountedNode, {
        videoId,
        playerVars: {
          autoplay: 1,
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
          start: Math.max(0, Math.floor(startAt)),
        },
        events: {
          onStateChange: (e) => {
            // 1 = playing, 2 = paused, 0 = ended
            if (e.data === 0) return; // ended — do not overwrite with an end-position
            if (e.data === 1) {
              pollRef.current = setInterval(persist, 5000);
            } else if (pollRef.current) {
              clearInterval(pollRef.current);
              pollRef.current = null;
              persist();
            }
          },
        },
      });
    });

    const onUnload = () => persist();
    window.addEventListener('beforeunload', onUnload);
    document.addEventListener('visibilitychange', onUnload);

    return () => {
      cancelled = true;
      persist();
      if (pollRef.current) clearInterval(pollRef.current);
      window.removeEventListener('beforeunload', onUnload);
      document.removeEventListener('visibilitychange', onUnload);
      try { playerRef.current && playerRef.current.destroy && playerRef.current.destroy(); } catch { /* noop */ }
      playerRef.current = null;
    };
  }, [videoId, episodeId, startAt]);

  return (
    <div className="video-container mb-4">
      <div ref={containerRef} className="rounded-xl overflow-hidden" data-testid="youtube-player-host" />
    </div>
  );
};

const EpisodePlayerModal = ({ episode, open, onClose, resumeFrom }) => {
  if (!episode) return null;

  const appleEmbed = getAppleEmbed(episode.apple_url);
  const hasYouTube = !!episode.youtube_id;

  // Pick a start position — explicit resumeFrom prop wins, then the saved progress.
  const savedProgress = getProgress(episode.id);
  const startAt = typeof resumeFrom === 'number' ? resumeFrom : (savedProgress?.seconds || 0);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#0f0f0f] border border-white/10 text-white p-0"
        data-testid="episode-player-modal"
      >
        <VisuallyHidden.Root asChild>
          <DialogTitle>{episode.title}</DialogTitle>
        </VisuallyHidden.Root>
        <div className="p-6">
          <h3 className="text-xl sm:text-2xl font-bold mb-4 pr-8">{episode.title}</h3>

          {hasYouTube ? (
            <YouTubePlayer videoId={episode.youtube_id} episodeId={episode.id} startAt={startAt} />
          ) : appleEmbed ? (
            <iframe
              src={appleEmbed}
              title={episode.title}
              allow="autoplay *; encrypted-media *;"
              className="w-full rounded-xl mb-4"
              style={{ height: '175px', border: 'none' }}
              data-testid="apple-embed-player"
            />
          ) : (
            <p className="text-gray-400 mb-4">Listen using the links below.</p>
          )}

          {/* Platform buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={episode.spotify_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="modal-spotify-link"
              className="flex-1 flex items-center justify-center gap-2 px-5 py-3 btn-red rounded-full text-sm font-semibold"
            >
              Listen on Spotify
            </a>
            <a
              href={episode.apple_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="modal-apple-link"
              className="flex-1 flex items-center justify-center gap-2 px-5 py-3 glass border-2 border-[#EF5B5B]/30 hover:border-[#EF5B5B] rounded-full text-sm font-semibold smooth"
            >
              Listen on Apple Podcasts
            </a>
            {episode.youtube_id && (
              <a
                href={`https://www.youtube.com/watch?v=${episode.youtube_id}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="modal-youtube-link"
                className="flex-1 flex items-center justify-center gap-2 px-5 py-3 glass border-2 border-[#EF5B5B]/30 hover:border-[#EF5B5B] rounded-full text-sm font-semibold smooth"
              >
                <Youtube className="w-4 h-4" /> Watch on YouTube
              </a>
            )}
          </div>

          {/* Host's Reflection */}
          {episode.reflection && episode.reflection.trim() && (
            <div className="mt-8 pt-6 border-t border-white/10" data-testid="episode-reflection">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-1.5 h-6 rounded-full bg-[#EF5B5B]" />
                <h4 className="text-lg font-bold">Host's Reflection</h4>
              </div>
              <div className="prose prose-invert prose-sm sm:prose-base max-w-none prose-headings:font-bold prose-a:text-[#EF5B5B] prose-p:text-gray-300 prose-p:leading-relaxed prose-strong:text-white prose-li:text-gray-300 prose-blockquote:border-l-[#EF5B5B] prose-blockquote:text-gray-300">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{episode.reflection}</ReactMarkdown>
              </div>
              {episode.reflection_slug && (
                <Link
                  to={`/blog/${episode.reflection_slug}`}
                  onClick={onClose}
                  data-testid="read-full-reflection"
                  className="mt-5 inline-flex items-center gap-2 px-6 py-3 btn-red rounded-full text-sm font-semibold"
                >
                  Read the full reflection <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EpisodePlayerModal;
